import { useEffect, useState } from "react";
import "./index.css";
import logo from "../../assets/images/logo-Photoroom.png";
import { Download } from "lucide-react";
import { useLocation } from "react-router-dom";
import ForgetPasswordModal from "./forget-password";
import { fireToastMessage } from "../toastContainer";
import useLoading from "../loader";
import { Button } from "bootstrap";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useAuth } from "../../hooks/auth";

const AccountPage = () => {
  const { loading, withLoading } = useLoading();
  const history = useHistory();

  const { signUp, signOut, signIn } = useAuth();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState(location.state?.acc || "Demo");
  const [isSignIn, setIsSignIn] = useState(false);

  useEffect(() => {
    if (location.state?.acc) {
      setActiveTab(location.state.acc);
      setIsSignIn(false);
    }
  }, [location.state]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    contactNo: "",
    country: "",
    city: "",
    role: "",
  });
  const [errors, setErrors] = useState({});

  const countries = ["USA", "UK", "Canada", "Germany", "Pakistan"];
  const cities = {
    USA: ["New York", "Los Angeles", "Chicago"],
    UK: ["London", "Manchester", "Birmingham"],
    Canada: ["Toronto", "Vancouver", "Montreal"],
    Germany: ["Berlin", "Munich", "Hamburg"],
    Pakistan: ["Karachi", "Lahore", "Islamabad"],
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" }); // Clear error when user types
  };

  const validateForm = () => {
    let newErrors = {};

    if (!isSignIn) {
      if (!formData.name.trim()) newErrors.name = "Name is required";
      if (!formData.country) newErrors.country = "Country is required";
      if (!formData.city) newErrors.city = "City is required";
      if (!formData.contactNo.trim() || !/^\d{10,}$/.test(formData.contactNo))
        newErrors.contactNo = "Valid contact number is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (
      !isSignIn &&
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(formData.password)
    ) {
      newErrors.password =
        "Password must be at least 8 characters long, contain an uppercase, a lowercase, and a number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    await withLoading(async () => {
      try {
        const payload = isSignIn
          ? { email: formData.email, password: formData.password }
          : {
              name: formData.name,
              email: formData.email,
              password: formData.password,
              contactNo: formData.contactNo,
              country: formData.country,
              city: formData.city,
              role: activeTab,
            };
        const response = !isSignIn
          ? await signUp(payload)
          : await signIn(payload);
        if (response.status === "error") {
          fireToastMessage({
            message: response?.message || "Something went wrong",
            type: "error",
          });
          return;
        }
        fireToastMessage({
          message: response?.message || "registered successfully",
          type: "success",
        });
        isSignIn && history.push("/demo-home");

        setIsSignIn(isSignIn ? false : true);
        setFormData({
          name: "",
          email: "",
          password: "",
          contactNo: "",
          country: "",
          city: "",
        });
      } catch (error) {
        console.log(error);
      }
    });
  };

  return (
    <div className="account-container">
      {/* Left Section - Downloads */}
      <div className="downloads-section">
        <h2>Downloads</h2>

        <h3>Account Opening Form (Individual)</h3>
        <ul>
          <li>
            <a href={"/img/CRF-Form.pdf"} rel="noreferrer" target="_blank">
              <Download className="download-icon" /> {"  "}Individual Account
              Opening Form{" "}
            </a>
          </li>
          <li>
            <a
              href={"/img/individual-form.pdf"}
              rel="noreferrer"
              target="_blank"
            >
              <Download className="download-icon" /> {"  "}KYC Form – normal
            </a>
          </li>
          <li>
            <a href={"/img/sahulat_form.pdf"} rel="noreferrer" target="_blank">
              <Download className="download-icon" /> {"  "}KYC Form – sahulat
            </a>
          </li>
          <li>
            <a
              href={"/img/Risk-Disclosure.pdf"}
              rel="noreferrer"
              target="_blank"
            >
              <Download className="download-icon" /> {"  "}Risk Disclosure
            </a>
          </li>
          {/* <li>
            <a href={"/img"} rel="noreferrer" target="_blank">
              <Download className="download-icon" /> {"  "}Commission Slab
            </a>
          </li> */}
        </ul>

        <h3>Document Requirement Individual</h3>
        <ul>
          <li>
            <a
              href={"/img/DOCUMENT REQUIRED FOR ACCOUNT OPENING.pdf"}
              rel="noreferrer"
              target="_blank"
            >
              <Download className="download-icon" /> {"  "}Document for
              individual
            </a>
          </li>
          {/* <li>
            <a href={"/img"} rel="noreferrer" target="_blank">
              <Download className="download-icon" /> {"  "}Self Employed
            </a>
          </li> */}
        </ul>

        <h3>Document Requirement Corporate</h3>
        <ul>
          <li>
            <a
              href={"/img/DOCUMENTS FOR ACCOUNTS OPENING (CORPORATE).pdf"}
              rel="noreferrer"
              target="_blank"
            >
              <Download className="download-icon" /> {"  "}Document for
              corporate
            </a>
          </li>
          {/* <li>
            <a href={"/img"} rel="noreferrer" target="_blank">
              <Download className="download-icon" /> {"  "}Self Employed
            </a>
          </li> */}
        </ul>

        <h3>Account Opening Forms (Corporate)</h3>
        <ul>
          <li>
            <a
              href={"/img/corporate_form.pdf"}
              rel="noreferrer"
              target="_blank"
            >
              <Download className="download-icon" /> {"  "}KYC Form – Corporate
            </a>
          </li>
          <li>
            <a
              href={"/img/Risk-Disclosure.pdf"}
              rel="noreferrer"
              target="_blank"
            >
              <Download className="download-icon" /> {"  "}Risk Disclosure
            </a>
          </li>
        </ul>
      </div>

      {/* Right Section - Form */}
      <div className="form-section">
        <div className="center">
          <img src={logo} alt="logo" className="navbar-new-logo" />
        </div>

        {!isSignIn ? (
          <div className="tabs">
            <p
              className={activeTab === "Demo" ? "active" : ""}
              onClick={() => setActiveTab("Demo")}
            >
              Demo Account
            </p>
            <p
              className={activeTab === "Real" ? "active" : ""}
              onClick={() => setActiveTab("Real")}
            >
              New Account
            </p>
          </div>
        ) : (
          <h3 className="signUp-para">Sign in to your account</h3>
        )}

        <form className="form-container" onSubmit={handleSubmit}>
          {!isSignIn && (
            <>
              <input
                type="text"
                name="name"
                placeholder="Name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? "is-invalid" : ""}
              />
              {errors.name && <div className="error">{errors.name}</div>}
            </>
          )}

          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? "is-invalid" : ""}
          />
          {errors.email && <div className="error">{errors.email}</div>}

          {isSignIn && (
            <p
              className="forgetPassword"
              style={{ cursor: "pointer" }}
              onClick={() => setShowModal(true)}
            >
              Forgot Password?
            </p>
          )}

          <input
            type="password"
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            className={errors.password ? "is-invalid" : ""}
          />
          {errors.password && <div className="error">{errors.password}</div>}

          {!isSignIn && (
            <>
              <input
                type="text"
                name="contactNo"
                placeholder="Contact No"
                value={formData.contactNo}
                onChange={handleChange}
                className={errors.contactNo ? "is-invalid" : ""}
              />
              {errors.contactNo && (
                <div className="error">{errors.contactNo}</div>
              )}

              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className={errors.country ? "is-invalid" : ""}
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
              {errors.country && <div className="error">{errors.country}</div>}

              <select
                name="city"
                value={formData.city}
                onChange={handleChange}
                disabled={!formData.country}
                className={errors.city ? "is-invalid" : ""}
              >
                <option value="">Select City</option>
                {formData.country &&
                  cities[formData.country]?.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
              </select>
              {errors.city && <div className="error">{errors.city}</div>}
            </>
          )}

          <button className="submit-btn" type="submit" disabled={loading}>
            {loading ? (
              <span className="loader"></span>
            ) : isSignIn ? (
              "Sign In"
            ) : (
              "Sign Up"
            )}
          </button>
        </form>

        <div className="signUp-section">
          <p>
            {isSignIn ? (
              <>
                Don't have an account?{" "}
                <button
                  href="#"
                  onClick={() => {
                    setIsSignIn(false);
                    setFormData({
                      name: "",
                      email: "",
                      password: "",
                      contactNo: "",
                      country: "",
                      city: "",
                      role: "Real",
                    });
                    setErrors({});
                  }}
                >
                  Sign Up
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  onClick={() => {
                    setIsSignIn(true);
                    setFormData({
                      email: "",
                      password: "",
                    });
                    setErrors({});
                  }}
                >
                  Sign In
                </button>
              </>
            )}
          </p>
        </div>
      </div>
      <ForgetPasswordModal
        show={showModal}
        handleClose={() => setShowModal(false)}
      />
    </div>
  );
};

export default AccountPage;
